import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { CodeDiscountSummary } from '../types';

interface DiscountCodeInputProps {
  codes: string[];
  onCodesChange: (codes: string[]) => void;
  calculating: boolean;
  codeDiscounts?: CodeDiscountSummary[];
}

export const DiscountCodeInput: React.FC<DiscountCodeInputProps> = ({
  codes,
  onCodesChange,
  calculating,
  codeDiscounts,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const codeStatusMap = useMemo(() => {
    if (!codeDiscounts || codeDiscounts.length === 0) {
      return new Map<string, CodeDiscountSummary>();
    }
    return new Map<string, CodeDiscountSummary>(
      codeDiscounts.map((entry) => [entry.code.toUpperCase(), entry])
    );
  }, [codeDiscounts]);
  const handledInvalidCodesRef = useRef<Map<string, string | undefined>>(new Map());

  useEffect(() => {
    if (!codeDiscounts) {
      return;
    }

    if (!codeDiscounts || codeDiscounts.length === 0) {
      handledInvalidCodesRef.current.clear();
      return;
    }

    const invalidEntries = codeDiscounts.filter((entry) => entry.valid === false);

    const currentHandled = handledInvalidCodesRef.current;
    const nextHandled = new Map<string, string | undefined>();

    const invalidCodesToRemove = new Set<string>();
    const toastMessages: string[] = [];

    invalidEntries.forEach((entry) => {
      const codeUpper = entry.code.toUpperCase();
      nextHandled.set(codeUpper, entry.reason);

      const reason = entry.reason || 'Discount code could not be applied';
      const previousReason = currentHandled.get(codeUpper);

      const isPendingCode = pendingCode && pendingCode === codeUpper;
      const shouldNotify =
        !currentHandled.has(codeUpper) ||
        previousReason !== reason ||
        Boolean(isPendingCode);

      if (shouldNotify) {
        toastMessages.push(`${entry.code}: ${reason}`);
      }

      invalidCodesToRemove.add(codeUpper);
    });

    if (toastMessages.length > 0) {
      window.alert(toastMessages.join('\n'));
    }

    if (invalidCodesToRemove.size > 0) {
      const filteredCodes = codes.filter(
        (code) => !invalidCodesToRemove.has(code.toUpperCase())
      );
      if (filteredCodes.length !== codes.length) {
        onCodesChange(filteredCodes);
      }
    }

    handledInvalidCodesRef.current = nextHandled;
    setPendingCode(null);
  }, [codeDiscounts, onCodesChange, pendingCode, codes]);

  useEffect(() => {
    if (codes.length === 0) {
      setError(null);
      setPendingCode(null);
    }
  }, [codes]);

  const handleAddCode = () => {
    const code = inputValue.trim().toUpperCase();

    if (!code) {
      setError('Please enter a discount code');
      return;
    }

    if (codes.includes(code)) {
      setError('This code is already applied');
      return;
    }

    const newCodes = [...codes, code];
    onCodesChange(newCodes);
    setInputValue('');
    setError(null);
    setPendingCode(code);
  };

  const handleRemoveCode = (codeToRemove: string) => {
    const newCodes = codes.filter((code) => code !== codeToRemove);
    onCodesChange(newCodes);
    setError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddCode();
    }
  };

  const renderChip = (code: string) => {
    const status = codeStatusMap.get(code.toUpperCase());
    const isInvalid = status?.valid === false;
    const isValid = status?.valid === true;
    const chipStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 8px',
      backgroundColor: isInvalid ? '#FDEBEC' : isValid ? '#E8F5E9' : '#F1E8E9',
      border: isInvalid
        ? '1px solid #dc3545'
        : isValid
          ? '1px solid #28a745'
          : '1px solid #C5A3A6',
      borderRadius: '4px',
      fontSize: '12px',
      color: isInvalid ? '#A61B1B' : isValid ? '#2F7A3E' : '#6F1920',
    } as React.CSSProperties;

    return (
      <span
        key={code}
        style={chipStyle}
        title={
          status
            ? status.valid
              ? 'Discount applied'
              : status.reason || 'Discount code could not be applied'
            : undefined
        }
      >
        {code}
        <button
          onClick={() => handleRemoveCode(code)}
          disabled={calculating}
          style={{
            marginLeft: '6px',
            background: 'none',
            border: 'none',
            color: isInvalid ? '#A61B1B' : '#6F1920',
            cursor: calculating ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            padding: 0,
            width: '16px',
            height: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label={`Remove ${code}`}
        >
          ×
        </button>
      </span>
    );
  };

  return (
    <div className="intan-discount-code-input" style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter discount code"
          disabled={calculating}
          style={{
            flex: 1,
            padding: '8px 12px',
            border: error ? '1px solid #dc3545' : '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '14px',
          }}
        />
        <button
          onClick={handleAddCode}
          disabled={calculating || !inputValue.trim()}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6F1920',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: calculating || !inputValue.trim() ? 'not-allowed' : 'pointer',
            opacity: calculating || !inputValue.trim() ? 0.6 : 1,
            fontSize: '14px',
          }}
        >
          Apply
        </button>
      </div>

      {error && (
        <div style={{ color: '#dc3545', fontSize: '12px', marginBottom: '8px' }}>
          {error}
        </div>
      )}

      {codes.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {codes.map(renderChip)}
        </div>
      )}
    </div>
  );
};

